import { useMemo, useState } from 'react';
import { Alert, Chip, Skeleton, Stack, Typography } from '@mui/material';
import {
  FeedbackOptions,
  type CreatePlanWizardValues,
} from '@/packages/idp/pages/create-plan/types';
import { fontWeights } from '@/@mui/tokens/font-weights';
import { ContentBox } from '@/components/shared/content-box';
import { useNotification } from '@/context/notification';
import { useWizard } from '@/context/wizard';
import { useUserJobsIndex } from '@/data/account/user-jobs';
import { getAllOccupationSkills } from '@/data/account/user-jobs/utils/get-all-occupation-skills';
import { useShowUserFeedback } from '@/data/guidance/feedback';
import { UserFeedbackStatus } from '@/data/guidance/feedback/constants';
import { useUpdateProfileBasedRecommendation } from '@/data/idp/profile-based-recommendations';
import { RecommendationPlanIds } from '@/data/idp/profile-based-recommendations/interfaces';
import type { Skill } from '@/models/taxonomy/skills';
import { StepActions } from '@/packages/idp/pages/create-plan/components/step-actions';

const SkillSkeleton = () => (
  <>
    <Skeleton variant="rounded" width={80} height={32} />
    <Skeleton variant="rounded" width={100} height={32} />
    <Skeleton variant="rounded" width={130} height={32} />
    <Skeleton variant="rounded" width={50} height={32} />
    <Skeleton variant="rounded" width={130} height={32} />
  </>
);

export const SkillSelection = () => {
  const { actions, values } = useWizard<CreatePlanWizardValues>();
  const { jobs, isFetching } = useUserJobsIndex();
  const { userFeedback, isPending: isFeedbackLoading } = useShowUserFeedback();
  const allOccupationSkills = getAllOccupationSkills(jobs);
  const [selectedSkills, setSelectedSkills] = useState<Skill[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { mutate, isPending } = useUpdateProfileBasedRecommendation(
    values?.id || ''
  );
  const { toast } = useNotification();

  const feedbackSkills = userFeedback?.skills_designations;
  const feedbackOption = values?.[RecommendationPlanIds.Feedback]?.option;
  const hasFeedbackSkills =
    (feedbackOption === FeedbackOptions.Current ||
      feedbackOption === FeedbackOptions.New) &&
    userFeedback?.inference_status === UserFeedbackStatus.Succeeded &&
    feedbackSkills &&
    feedbackSkills.length > 0;

  const feedbackSkillIds = useMemo(
    () => new Set(feedbackSkills?.map((s) => s.skill.id) ?? []),
    [feedbackSkills]
  );

  const occupationSkills = useMemo(
    () => allOccupationSkills?.filter((s) => !feedbackSkillIds.has(s.id)),
    [allOccupationSkills, feedbackSkillIds]
  );

  const isSkillSelected = (skill: Skill) =>
    selectedSkills.some((s) => s.id === skill.id);

  const handleSkillToggle = (skill: Skill) => {
    if (error) setError(null);

    if (isSkillSelected(skill)) {
      setSelectedSkills(selectedSkills.filter((s) => s.id !== skill.id));
      return;
    }

    if (selectedSkills.length < 5) {
      setSelectedSkills([...selectedSkills, skill]);
    }
  };

  const handleFeedbackSkillToggle = (skill: Skill) => {
    handleSkillToggle(skill);
  };

  const handleBack = () => {
    const feedbackValue = values?.[RecommendationPlanIds.Feedback];
    const backToNew =
      feedbackValue?.option === FeedbackOptions.New || !feedbackValue?.option;

    if (backToNew) {
      actions.navigate(RecommendationPlanIds.NewFeedback);
      return;
    }

    actions.navigate(RecommendationPlanIds.Feedback);
  };

  const handleNext = () => {
    if (!selectedSkills.length) {
      setError('Selecione pelo menos uma habilidade para continuar.');
      return;
    }

    mutate(
      {
        current_step: RecommendationPlanIds.RecommendationReview,
        developing_skills: selectedSkills,
      },
      {
        onSuccess: () => {
          actions.updateValues({
            [RecommendationPlanIds.SkillSelection]: selectedSkills,
            [RecommendationPlanIds.RecommendationReview]: null,
          });
          actions.navigate(RecommendationPlanIds.RecommendationReview);
        },
        onError: () => {
          toast({
            content:
              'Erro ao atualizar as habilidades do plano. Tente novamente mais tarde.',
            type: 'error',
          });
        },
      }
    );
  };

  return (
    <ContentBox>
      <Typography variant="h2">
        Quais habilidades você deseja desenvolver com este plano?
      </Typography>
      <Typography mt={1}>
        Com base no seu feedback e no seu objetivo de carreira, separamos
        algumas sugestões. <br />
        Escolha até 5.
      </Typography>
      {hasFeedbackSkills && (
        <Stack gap={1} mt={3}>
          <Typography variant="h3">O que seu feedback revelou</Typography>
          <Stack direction="row" gap={1.5} mt={1} flexWrap="wrap">
            {isFeedbackLoading && <SkillSkeleton />}
            {feedbackSkills.map((designation) => (
              <Chip
                variant={
                  isSkillSelected(designation.skill) ? 'filled' : 'outlined'
                }
                key={designation.id}
                label={designation.skill.name_pt}
                onClick={() => handleFeedbackSkillToggle(designation.skill)}
              />
            ))}
          </Stack>
        </Stack>
      )}
      <Stack gap={1} mt={3}>
        <Typography variant="h3">
          Habilidades relevantes para o seu desenvolvimento
        </Typography>
        <Stack direction="row" gap={1.5} mt={1} flexWrap="wrap">
          {isFetching && <SkillSkeleton />}
          {occupationSkills?.map((skill) => (
            <Chip
              variant={isSkillSelected(skill) ? 'filled' : 'outlined'}
              key={skill.id}
              label={skill.name_pt}
              onClick={() => handleSkillToggle(skill)}
            />
          ))}
        </Stack>
      </Stack>
      <Stack gap={1} direction="row" mt={3}>
        <Typography>Selecionados:</Typography>
        <Typography
          fontWeight={fontWeights.fontWeightBold}
          color="primary.dark"
        >
          {selectedSkills.length}/5
        </Typography>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}
      <StepActions
        onBack={handleBack}
        onNext={handleNext}
        nextLoading={isPending}
      />
    </ContentBox>
  );
};
